import { z } from 'zod';
import { type LeaderboardDefinition, manifestIdSchema } from './manifest';

export type LeaderboardReset = LeaderboardDefinition['reset'];

const DAY_MS = 24 * 60 * 60 * 1000;
const pad = (n: number) => String(n).padStart(2, '0');

/** ISO week of a date (weeks start on Monday, UTC): `2026-W44`. */
function isoWeek(date: Date): string {
  const day = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()));
  // The Thursday of this week decides the week's year.
  day.setUTCDate(day.getUTCDate() + 3 - ((day.getUTCDay() + 6) % 7));
  const yearStart = new Date(Date.UTC(day.getUTCFullYear(), 0, 1));
  const week = Math.ceil(((day.getTime() - yearStart.getTime()) / DAY_MS + 1) / 7);
  return `${day.getUTCFullYear()}-W${pad(week)}`;
}

/**
 * The period a score counts in, by the board's reset (UTC): `all`, `2026-10-26`, `2026-W44`
 * or `2026-10`. A new period starts an empty board; past ones stay as archives.
 */
export function leaderboardPeriod(reset: LeaderboardReset, date = new Date()): string {
  switch (reset) {
    case 'daily':
      return `${date.getUTCFullYear()}-${pad(date.getUTCMonth() + 1)}-${pad(date.getUTCDate())}`;
    case 'weekly':
      return isoWeek(date);
    case 'monthly':
      return `${date.getUTCFullYear()}-${pad(date.getUTCMonth() + 1)}`;
    default:
      return 'all';
  }
}

/** The period before the current one (the same `all` for boards that never reset). */
export function previousLeaderboardPeriod(reset: LeaderboardReset, date = new Date()): string {
  switch (reset) {
    case 'daily':
      return leaderboardPeriod(reset, new Date(date.getTime() - DAY_MS));
    case 'weekly':
      return leaderboardPeriod(reset, new Date(date.getTime() - 7 * DAY_MS));
    case 'monthly':
      return leaderboardPeriod(
        reset,
        new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth() - 1, 1)),
      );
    default:
      return 'all';
  }
}

/** A score as people read it: `12,850`, or for time boards (milliseconds) `1:02.345`. */
export function formatScore(score: number, format: 'number' | 'time'): string {
  if (format === 'number') return score.toLocaleString('en');
  const ms = Math.max(0, Math.round(score));
  const minutes = Math.floor(ms / 60_000);
  const seconds = Math.floor((ms % 60_000) / 1000);
  return `${minutes}:${String(seconds).padStart(2, '0')}.${String(ms % 1000).padStart(3, '0')}`;
}

/** How a board looks to the game. */
export interface LeaderboardInfo {
  id: string;
  name: string;
  sort: 'asc' | 'desc';
  format: 'number' | 'time';
  reset: LeaderboardReset;
  period: string;
}

/** One row of a board, as the game sees it. */
export interface LeaderboardEntry {
  rank: number;
  /** Stable for this player in this game only. */
  playerId: string;
  name: string;
  score: number;
  /** True for the player asking. */
  me: boolean;
}

export interface LeaderboardView {
  board: LeaderboardInfo;
  entries: LeaderboardEntry[];
}

/** What `leaderboards.submit` answers. */
export interface ScoreResult {
  /** The player's best score this period, after this one. */
  best: number;
  rank: number;
  /** Whether this score beat the player's previous best. */
  improved: boolean;
  /** The board's order, so the game page can tell a score that can't beat `best` (M36). */
  sort: 'asc' | 'desc';
  /** Live events' own boards the score also counted on (M37), by id. */
  alsoOn?: string[];
  /**
   * When an event next turns on or off (ISO), so the game page knows its answer only until
   * then; null when nothing is planned.
   */
  changesAt?: string | null;
}

export const leaderboardIdSchema = manifestIdSchema;

const limitSchema = z
  .number()
  .finite()
  .refine((value) => Math.abs(value) <= 1e15, 'Limits can be up to a million billion')
  .nullable();

/**
 * A board as the developer makes it in the dashboard (decided Oct 26, 2026). Order, format and
 * reset lock once the board has scores.
 */
const leaderboardFieldsSchema = z.object({
  name: z.string().trim().min(1, 'Give the board a name').max(60),
  sort: z.enum(['asc', 'desc']),
  format: z.enum(['number', 'time']),
  reset: z.enum(['none', 'daily', 'weekly', 'monthly']),
  min: limitSchema,
  max: limitSchema,
});
export const leaderboardSettingsSchema = leaderboardFieldsSchema.refine(
  (board) => board.min === null || board.max === null || board.min < board.max,
  { message: 'The lowest allowed score must be below the highest', path: ['max'] },
);
/** Only the settings to change (the CLI's edit, M18b); checked again once merged. */
export const leaderboardChangesSchema = leaderboardFieldsSchema.partial();
export type LeaderboardSettings = z.infer<typeof leaderboardSettingsSchema>;
export const newLeaderboardSchema = z.intersection(
  z.object({ id: leaderboardIdSchema }),
  leaderboardSettingsSchema,
);
export const scoreSchema = z
  .number()
  .finite('A score is a number')
  .refine((score) => Math.abs(score) <= 1e15, 'Scores can be up to a million billion');

/** Bridge parameters for the `leaderboards.*` methods. */
export const submitScoreParamsSchema = z
  .object({ id: leaderboardIdSchema, score: scoreSchema })
  .strict();
export const leaderboardViewParamsSchema = z
  .object({
    id: leaderboardIdSchema,
    period: z.enum(['current', 'previous']).default('current'),
    limit: z.number().int().min(1).max(50).default(10),
  })
  .strict();
